import { extractAddressFromGoogleResult } from '@my/ui';

/** A geocoded address, shaped for `directoryApi.createAddress`. */
export interface GeocodedAddress {
  placeId: string;
  name: string;
  fullAddress: string;
  city: string | null;
  postcode: string | null;
  countryIsoCode: string;
  latitude: number | null;
  longitude: number | null;
}

/**
 * Forward-geocode a free-text address against the browser Google Geocoder (the
 * SDK is already loaded app-wide via `<APIProvider>`). Returns null when the
 * address can't be resolved to a real place with a country — we never fabricate
 * a place id (see the onboarding gotcha), so an unresolved address can't be
 * turned into a Location and its order fails at commit.
 */
export async function geocodeAddress(
  geocoder: google.maps.Geocoder,
  address: string,
): Promise<GeocodedAddress | null> {
  const full = address.trim();
  if (!full) return null;

  let result: google.maps.GeocoderResult | null = null;
  try {
    const { results } = await geocoder.geocode({ address: full });
    result = results?.[0] ?? null;
  } catch {
    return null; // ZERO_RESULTS / OVER_QUERY_LIMIT / transient
  }
  if (!result) return null;

  const x = extractAddressFromGoogleResult(result);
  if (!x.placeId || !x.countryIsoCode) return null;

  return {
    placeId: x.placeId,
    name: full.split(',')[0]!.trim() || full,
    fullAddress: full,
    city: x.city ?? null,
    postcode: x.postcode ?? null,
    countryIsoCode: x.countryIsoCode,
    latitude: x.latitude ?? null,
    longitude: x.longitude ?? null,
  };
}
